import { ArrowRight, Check } from "lucide-react";
import { IMAGES, SERVICES, DESTINATIONS } from "@/data/content";
import KineticHero from "@/components/KineticHero";
import ImageReveal from "@/components/ImageReveal";
import MagneticButton from "@/components/MagneticButton";
import SplitText from "@/components/SplitText";
import Marquee from "@/components/Marquee";
import Reveal from "@/components/Reveal";

const SERVICE_IMAGES = [IMAGES.strategy, IMAGES.condoPool, IMAGES.villaAlt];

export default function Services() {
  return (
    <div data-testid="services-page" className="overflow-x-clip">
      <KineticHero
        eyebrow="The Core Framework"
        image={IMAGES.villaPink}
        lines={[
          "A deliberate",
          "system for",
          <span key="s" className="italic text-gl-gold">cross-border ownership.</span>,
        ]}
        subtitle="Three integrated pillars that move you from financial foundations to a closed international purchase."
      />

      <section className="py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12 space-y-28 md:space-y-40">
          {SERVICES.map((s, i) => {
            const reversed = i % 2 === 1;
            return (
              <div
                key={s.id}
                data-testid={`service-detail-${s.id}`}
                className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center"
              >
                <div className={reversed ? "lg:order-2" : ""}>
                  <ImageReveal
                    src={SERVICE_IMAGES[i]}
                    alt={s.title}
                    className="aspect-[4/3] w-full"
                  />
                </div>
                <div className={reversed ? "lg:order-1" : ""}>
                  <Reveal>
                    <span className="font-serif-display text-7xl md:text-8xl text-stroke-gold leading-none block mb-4">
                      {s.id}
                    </span>
                  </Reveal>
                  <SplitText
                    as="h2"
                    animateOnLoad={false}
                    className="font-serif-display text-3xl md:text-5xl font-light text-gl-text leading-[1.05]"
                    lines={[s.title]}
                  />
                  <Reveal delay={0.12}>
                    <p className="text-gl-gold/90 font-light mt-4 mb-5">{s.tagline}</p>
                    <p className="text-gl-text2 text-lg font-light leading-relaxed max-w-lg">
                      {s.description}
                    </p>
                  </Reveal>
                  <div className="mt-8 space-y-3">
                    {s.points.map((p, j) => (
                      <Reveal key={p} delay={0.18 + j * 0.06}>
                        <div className="flex items-center gap-3">
                          <span className="w-5 h-5 flex-shrink-0 border border-gl-gold/50 flex items-center justify-center">
                            <Check size={12} className="text-gl-gold" />
                          </span>
                          <span className="text-gl-text2 font-light">{p}</span>
                        </div>
                      </Reveal>
                    ))}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* MARQUEE */}
      <section className="bg-gl-bg2 border-y border-gl-gold/15 py-8 md:py-12">
        <Marquee
          items={DESTINATIONS}
          reverse
          duration={44}
          className="font-serif-display text-3xl md:text-5xl text-gl-text/90 font-light"
          itemClassName="italic"
        />
      </section>

      {/* CTA */}
      <section className="relative py-28 md:py-40 overflow-hidden">
        <div className="absolute inset-0">
          <img src={IMAGES.heroDusk} alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gl-bg/88" />
          <div className="absolute inset-0 bg-gl-primary/25 mix-blend-multiply" />
        </div>
        <div className="relative max-w-4xl mx-auto px-6 md:px-12 text-center">
          <SplitText
            as="h2"
            animateOnLoad={false}
            className="font-serif-display text-4xl md:text-6xl font-light text-gl-text leading-[1.05]"
            lines={["Which pillar is", <span key="s" className="italic text-gl-gold">your starting point?</span>]}
          />
          <Reveal delay={0.18}>
            <p className="mt-7 text-gl-text2 text-lg font-light max-w-2xl mx-auto">
              In your strategy call, we'll diagnose exactly where you are and
              build the roadmap from there.
            </p>
          </Reveal>
          <Reveal delay={0.3}>
            <div className="mt-11 flex justify-center">
              <MagneticButton
                to="/contact"
                data-testid="services-cta"
                className="px-9 py-4 bg-gl-gold text-gl-bg font-medium tracking-wide hover:bg-gl-goldHover transition-colors duration-300"
              >
                Schedule Your Strategy Call
                <ArrowRight size={18} />
              </MagneticButton>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
